import { Component, OnInit } from '@angular/core';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from '../../core/services/previous-route.service';
import { Router } from '@angular/router';

/**
 * @component HomeComponent
 * @description Main homepage component serving as the primary entry point for authenticated users.
 * Manages route tracking for navigation history and device type detection for responsive behavior.
 * Automatically stores the current route path for later navigation fallback.
 * 
 * Responsibilities:
 * - Detect device type (mobile vs desktop) based on user agent
 * - Track and store the current route in the navigation history service
 * - Provide responsive UI behavior based on device capabilities
 * - Serve as the root component for authenticated user sessions
 * 
 * Key Features:
 * - Mobile device detection using user agent parsing
 * - Navigation history tracking via PreviousRouteService
 * - Location-aware component initialization
 * - Responsive layout considerations for different device types
 * 
 * @selector app-home
 * @template ./home.component.html
 * @styleUrl ./home.component.scss
 * @implements {OnInit}
 */
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  /**
   * @property {boolean} isMobileDevice
   * @description Flag indicating whether the application is running on a mobile device.
   * Determined by checking navigator.userAgent against common mobile device patterns.
   * Used to apply responsive styling, layout adjustments, and mobile-specific features.
   */
  isMobileDevice: boolean = false;

  /**
   * @method constructor
   * @description Initializes the component with required Angular services for route tracking
   * and navigation management.
   * 
   * @param {LocationStrategy} locationStrategy - Angular LocationStrategy service for accessing
   * and managing the current URL path. Used to capture the current route location.
   * @param {PreviousRouteService} previousRouteService - Custom service for managing navigation history
   * and storing previous route information for fallback navigation scenarios.
   * @param {Router} router - Angular Router service for programmatic navigation between routes.
   * Currently configured but can be utilized for conditional routing based on device type.
   */
  constructor(
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService,
    private router: Router
  ) { }

  /**
   * @method ngOnInit
   * @description Angular lifecycle hook called when the component is initialized.
   * Performs initialization operations:
   * 1. Detects device type (mobile vs desktop) using user agent analysis
   * 2. Stores the current route path in navigation history for fallback navigation
   * 
   * The previous route is stored to enable "back" navigation functionality or
   * to fallback to the previous page if the current route becomes unavailable.
   * 
   * @returns {void}
   */
  public ngOnInit(): void {
    this.checkIfMobileDevice();
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path()); // To store the Previous route
  }

  /**
   * @method checkIfMobileDevice
   * @description Detects whether the application is running on a mobile device by analyzing
   * the browser's user agent string. Checks for common mobile device patterns including
   * Android devices, iOS devices (iPhone, iPad, iPod), and mobile browsers.
   * 
   * Detected Device Types:
   * - Android devices (phones, tablets, wearables)
   * - iOS devices (iPhone, iPad, iPod Touch)
   * - Opera Mini browser
   * - IE Mobile browser
   * - Windows Phone Desktop
   * 
   * The result is stored in isMobileDevice property for use in responsive layouts
   * and mobile-specific feature implementations.
   * 
   * Note: User agent detection can be spoofed and is not 100% reliable. Consider
   * using feature detection or server-side detection for critical functionality.
   * 
   * @returns {void}
   * 
   * @example
   * // Called automatically in ngOnInit
   * this.checkIfMobileDevice();
   * 
   * // Check the result
   * if (this.isMobileDevice) {
   *   // Apply mobile-specific styles or logic
   * }
   */
  checkIfMobileDevice(): void {
    this.isMobileDevice = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
    // if (this.isMobileDevice) {
    //   this.router.navigate(['/home/visits']);
    // }
  }
}


